import { AudioError } from '@/state/presenters';
import { IVoice } from '@d-id/types';
import { useCallback, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { activePlayerIdAtom, PlayerMode, playerModeAtom } from './useAudioPlayer';

interface Props {
    voice: IVoice | null;
    playerId?: string;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export function useAudioFromUrl({ voice, playerId = '', ttsError, setTtsError }: Props) {
    const [playerMode, setPlayerMode] = useRecoilState(playerModeAtom);
    const setActivePlayerId = useSetRecoilState(activePlayerIdAtom);
    const [pause, setPause] = useState<VoidFunction>();

    const play = useCallback(async (url: string) => {
        if (playerMode !== PlayerMode.Ready && playerMode !== PlayerMode.NotLoaded) {
            return;
        }

        setPlayerMode(PlayerMode.Loading);
        setActivePlayerId(playerId);

        const audio = new Audio(url);

        audio.onplaying = () => {
            setPlayerMode(PlayerMode.Playing);
            setActivePlayerId(playerId);
        };

        audio.onended = () => {
            audio.remove();
            setPlayerMode(PlayerMode.Ready);
            setActivePlayerId('');
        };

        audio.addEventListener('error', error => {
            console.error('Error during voice playing', error);
            setTtsError?.('unknown');
        });

        audio.play();

        return audio;
    }, []);

    return {
        playerMode,
        error: ttsError,
        pause,
        play: async () => {
            setActivePlayerId(playerId);
            if (voice?.previewUrl) {
                const player = await play(voice.previewUrl);

                setPause(() => () => {
                    setPlayerMode(PlayerMode.Ready);
                    setActivePlayerId('');
                    player?.pause();
                    player?.remove();
                });
            }
        },
    };
}
